import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toPoint, forward as toMGRS } from "mgrs";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import GpsFixedRoundedIcon from "@mui/icons-material/GpsFixedRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import RadarRoundedIcon from "@mui/icons-material/RadarRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import { GUN_OPTIONS, BACKEND_URL } from "../utils/constants";
import {
  tacticalActionSx,
  tacticalCardSx,
  tacticalChipSx,
  tacticalMetricSx,
} from "../utils/fdcTheme";
import { formatTimeOnly, SHOT_TIMELINE_STAGES, latestStageOf } from "../utils/fdcHelpers";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function parseMgrs(raw) {
  const s = (raw || "").trim();
  if (!s) return null;
  const m = s.match(/^([0-9]{1,2}[C-HJ-NP-X][A-Z]{2}[0-9]{2,})/i);
  if (!m) return null;
  const mgrs = m[1].toUpperCase().replace(/\s+/g, "");
  const digits = mgrs.replace(/^[0-9]{1,2}[C-HJ-NP-X][A-Z]{2}/i, "");
  if (digits.length % 2 !== 0) return null;
  try {
    const [lon, lat] = toPoint(mgrs);
    return { mgrs, lat, lon };
  } catch {
    return null;
  }
}

function positionToMgrs(pos) {
  if (!pos || pos.norths == null || pos.wests == null) return null;
  try {
    return toMGRS([pos.wests, pos.norths], 4); // 8-digit = 10m precision, compact
  } catch {
    return null;
  }
}

function StatusChip({ label, tone = "neutral" }) {
  const theme = useTheme();
  return <Chip size="small" label={label} sx={tacticalChipSx(theme, tone)} />;
}

const iconBtnSx = (theme) => ({
  width: 36, height: 36,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.6),
  bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.06 : 0.03),
  color: theme.palette.text.primary,
  "&:hover": { bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.12 : 0.07) },
});

/* ------------------------------------------------------------------ */
/*  GPS Proximity Hook                                                 */
/* ------------------------------------------------------------------ */

function useGpsProximity(savedPosition, thresholdMeters = 150) {
  const [drift, setDrift] = useState(null);
  const [checking, setChecking] = useState(false);
  const intervalRef = useRef(null);

  const check = useCallback(() => {
    if (!savedPosition || !navigator?.geolocation) return;
    setChecking(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const d = haversineMeters(
          savedPosition.norths, savedPosition.wests,
          pos.coords.latitude, pos.coords.longitude,
        );
        setDrift(Math.round(d));
        setChecking(false);
      },
      () => { setDrift(null); setChecking(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  }, [savedPosition]);

  useEffect(() => {
    check();
    intervalRef.current = setInterval(check, 60000);
    return () => clearInterval(intervalRef.current);
  }, [check]);

  const needsUpdate = drift !== null && drift > thresholdMeters;
  return { drift, needsUpdate, checking, recheck: check };
}

/* ------------------------------------------------------------------ */
/*  Dialogs                                                            */
/* ------------------------------------------------------------------ */

/* Shared sx to keep dialogs compact on mobile */
const compactDialogPaper = {
  mx: 2,
  my: "auto",
  maxHeight: "calc(100vh - 48px)",
  width: "calc(100% - 32px)",
  borderRadius: 3,
};

function GunDialog({ open, onClose, current, onChange }) {
  const handlePick = (v) => { onChange(v); onClose(); };
  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth="xs"
      slotProps={{ paper: { sx: compactDialogPaper } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, py: 1.25, px: 2 }}>Select Gun</DialogTitle>
      <List dense sx={{ pt: 0, overflow: "auto" }}>
        {GUN_OPTIONS.map((g) => (
          <ListItemButton
            key={g.value}
            selected={g.value === current}
            onClick={() => handlePick(g.value)}
            sx={{ py: 0.75 }}
          >
            <ListItemText primary={g.label} primaryTypographyProps={{ fontWeight: g.value === current ? 700 : 600, fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>
    </Dialog>
  );
}

function MissionDialog({ open, onClose, current, options, onChange }) {
  const handlePick = (v) => { onChange(v); onClose(); };
  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth="xs"
      slotProps={{ paper: { sx: compactDialogPaper } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, py: 1.25, px: 2 }}>Select Mission</DialogTitle>
      {!options.length ? (
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>No missions available yet.</Typography>
        </DialogContent>
      ) : (
        <List dense sx={{ pt: 0, overflow: "auto" }}>
          {options.map((m) => (
            <ListItemButton
              key={m.mission_id}
              selected={m.mission_id === current}
              onClick={() => handlePick(m.mission_id)}
              sx={{ py: 0.75 }}
            >
              <ListItemText
                primary={m.mission_id}
                secondary={m.description || null}
                primaryTypographyProps={{ fontWeight: m.mission_id === current ? 700 : 600, fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Dialog>
  );
}

function PositionDialog({ open, onClose, gunNumber, onSaved }) {
  const theme = useTheme();
  const [mgrs, setMgrs] = useState("");
  const [alt, setAlt] = useState("");
  const [error, setError] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleGps = () => {
    if (!navigator?.geolocation) return;
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          const m = toMGRS([pos.coords.longitude, pos.coords.latitude], 5);
          setMgrs(m);
          if (typeof pos.coords.altitude === "number") setAlt(pos.coords.altitude.toFixed(2));
        } catch { /* */ }
        setGpsBusy(false);
      },
      () => setGpsBusy(false),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const handleSubmit = async () => {
    setError("");
    const p = parseMgrs(mgrs);
    if (!p) { setError("Invalid MGRS"); return; }
    const altitude = parseFloat((alt || "").replace(",", "."));
    if (!Number.isFinite(altitude)) { setError("Invalid altitude"); return; }
    try {
      await axios.post(`${BACKEND_URL}/latest-gun-position`, {
        gun_number: gunNumber,
        Norths: Number(p.lat.toFixed(7)),
        wests: Number(p.lon.toFixed(7)),
        altitude: Number(altitude.toFixed(2)),
      });
      setSuccess(true);
      if (onSaved) onSaved();
      setTimeout(() => { setSuccess(false); setMgrs(""); setAlt(""); onClose(); }, 800);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed");
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth="xs"
      slotProps={{ paper: { sx: compactDialogPaper } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, py: 1.25, px: 2 }}>
        Update Gun {gunNumber} Position
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1 }}>
        <Stack spacing={1.25}>
          {error && <Alert severity="error" sx={{ py: 0, fontWeight: 700 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ py: 0, fontWeight: 700 }}>Saved!</Alert>}
          <TextField
            size="small" fullWidth label="MGRS Coordinates" placeholder="29SND6264358593"
            value={mgrs} onChange={(e) => { setMgrs(e.target.value); setError(""); }}
          />
          <TextField
            size="small" fullWidth label="Altitude (m)" placeholder="167"
            value={alt} onChange={(e) => { setAlt(e.target.value); setError(""); }}
          />
          <Button
            size="small" variant="outlined" disabled={gpsBusy} onClick={handleGps}
            startIcon={<MyLocationIcon />}
            sx={{ ...tacticalActionSx(theme, "warning"), fontSize: 13 }}
          >
            {gpsBusy ? "Acquiring..." : "Use GPS"}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button>
        <Button
          size="small" variant="contained" onClick={handleSubmit}
          disabled={!mgrs.trim() || !alt.trim()}
          endIcon={<SendIcon />}
          sx={{ ...tacticalActionSx(theme, "success", "contained"), textTransform: "none", fontWeight: 600 }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Gun Commander Page                                            */
/* ------------------------------------------------------------------ */

export default function GunCmdPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [myGunNumber, setMyGunNumber] = useState(() => {
    try { return localStorage.getItem("guncmd_myGun") || ""; } catch { return ""; }
  });

  useEffect(() => {
    if (!myGunNumber) navigate("/guncmd/setup", { replace: true });
  }, [myGunNumber, navigate]);

  // Missions
  const [missionOptions, setMissionOptions] = useState([]);
  const [missionId, setMissionId] = useState(() => {
    try { return localStorage.getItem("guncmd_selectedMission") || ""; } catch { return ""; }
  });

  useEffect(() => {
    try {
      if (missionId) localStorage.setItem("guncmd_selectedMission", missionId);
      else localStorage.removeItem("guncmd_selectedMission");
    } catch { /* ignore */ }
  }, [missionId]);
  const [missionsLastUpdated, setMissionsLastUpdated] = useState(null);
  const [missionsLastError, setMissionsLastError] = useState("");

  // Shots
  const [shots, setShots] = useState([]);
  // Mission-level state pushed by the FDC: who is currently regulating, etc.
  const [regulatingGunId, setRegulatingGunId] = useState(null);
  // Which history shot's timeline tooltip is open (touch-friendly click-to-toggle).
  const [openTimelineShotId, setOpenTimelineShotId] = useState(null);
  useEffect(() => {
    if (openTimelineShotId == null) return;
    const t = setTimeout(() => setOpenTimelineShotId(null), 6000);
    return () => clearTimeout(t);
  }, [openTimelineShotId]);
  const [statusMsg, setStatusMsg] = useState("");

  // Poll mission state so we know who's currently regulating.
  useEffect(() => {
    if (!missionId) { setRegulatingGunId(null); return; }
    let cancelled = false, inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await axios.get(`${BACKEND_URL}/mission-state/${missionId}`, { timeout: 10000 });
        const reg = res.data?.data?.regulating_gun_id ?? null;
        if (!cancelled) setRegulatingGunId(reg ? String(reg) : null);
      } catch { /* ignore — keep last known value */ }
      finally { inFlight = false; }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [missionId]);

  const iAmRegulating = regulatingGunId && String(regulatingGunId) === String(myGunNumber);

  // Alerts
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [newElementsBanner, setNewElementsBanner] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const bannerTimerRef = useRef(null);
  const lastSeenKeyRef = useRef("");
  const lastSeenShotIdRef = useRef(null);

  // Position
  const [savedPosition, setSavedPosition] = useState(null);
  const [positionVersion, setPositionVersion] = useState(0);

  // Dialogs
  const [gunDialogOpen, setGunDialogOpen] = useState(false);
  const [posDialogOpen, setPosDialogOpen] = useState(false);
  const [missionDialogOpen, setMissionDialogOpen] = useState(false);

  // Landscape
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    const update = () => setIsLandscape(window.matchMedia?.("(orientation: landscape)")?.matches || false);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // GPS proximity
  const gps = useGpsProximity(savedPosition, 150);

  // Fetch saved position
  useEffect(() => {
    if (!myGunNumber) return;
    axios.get(`${BACKEND_URL}/latest-gun-positions`, { timeout: 10000 })
      .then((res) => {
        const guns = res.data?.data || [];
        setSavedPosition(guns.find((g) => String(g.gun_number) === String(myGunNumber)) || null);
      })
      .catch(() => {});
  }, [myGunNumber, positionVersion]);

  // Vibrate
  const vibrateUpdate = () => {
    if (!alertsEnabled) return;
    try { navigator?.vibrate?.([150, 60, 150]); } catch { /* */ }
  };

  const showNewBannerAndPulse = () => {
    if (alertsEnabled) {
      setNewElementsBanner(true);
      setPulseKey((k) => k + 1);
      vibrateUpdate();
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = setTimeout(() => { setNewElementsBanner(false); bannerTimerRef.current = null; }, 6500);
    } else {
      setPulseKey((k) => k + 1);
    }
  };

  // Poll missions
  useEffect(() => {
    let cancelled = false, inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await axios.get(`${BACKEND_URL}/fetch-data`, { timeout: 15000 });
        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          setMissionOptions(rows.map((item) => ({
            mission_id: item[1],
            description: `${item[10] || ""} - ${item[11] || ""}`.trim(),
          })));
          setMissionsLastUpdated(new Date());
          setMissionsLastError("");
        }
      } catch { if (!cancelled) { setMissionOptions([]); setMissionsLastError("Sync failed"); } }
      finally { inFlight = false; }
    };
    load();
    const id = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Reset on gun/mission change
  useEffect(() => {
    const key = `${missionId}::${myGunNumber}`;
    if (lastSeenKeyRef.current !== key) {
      lastSeenKeyRef.current = key;
      lastSeenShotIdRef.current = null;
      setNewElementsBanner(false);
      if (bannerTimerRef.current) { clearTimeout(bannerTimerRef.current); bannerTimerRef.current = null; }
    }
  }, [missionId, myGunNumber]);

  // Poll shots
  useEffect(() => {
    if (!missionId || !myGunNumber) { setShots([]); return; }
    let cancelled = false, inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await axios.get(`${BACKEND_URL}/shots/${missionId}?confirmed_only=true`, { timeout: 15000 });
        const all = Array.isArray(res.data) ? res.data : [];
        // Keep ALL mission shots so non-regulating guns can follow the reg gun's elements during AF.
        // Mark each row as "mine" so the UI can decide whether to show the lifecycle action buttons.
        const annotated = all
          .map((s) => ({ ...s, _mine: String(s.gun_id) === String(myGunNumber) }))
          .sort((a, b) => (b.shot_id ?? 0) - (a.shot_id ?? 0));
        if (!cancelled) {
          setShots(annotated);
          const newestId = annotated[0]?.shot_id ?? null;
          if (lastSeenShotIdRef.current !== null && newestId !== null && newestId !== lastSeenShotIdRef.current) showNewBannerAndPulse();
          if (newestId !== null) lastSeenShotIdRef.current = newestId;
          setStatusMsg(annotated.length ? `Gun ${myGunNumber} · Mission ${missionId}` : "Waiting for FDC");
        }
      } catch { if (!cancelled) setStatusMsg("Error loading fire elements."); }
      finally { inFlight = false; }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [missionId, myGunNumber, alertsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // The fire-elements card shows MY latest shot, not the mission-wide latest.
  // Only when this gun has no shots yet (e.g. AF phase where only the reg gun fires)
  // do we fall back to the most recent mission-wide shot for "follow only" mode.
  const myShots = shots.filter((s) => s._mine);
  const latest = myShots[0] || null;
  const missionLatest = shots[0] || null;
  const followingShot = !latest && missionLatest ? missionLatest : null;
  // Combined "what to display" — my latest if I have one, else the reg gun's shot to follow.
  const displayShot = latest || followingShot;
  const inFollowMode = !latest && !!followingShot;
  const bigValueSize = isLandscape ? { xs: 42, sm: 58 } : { xs: 34, sm: 46 };

  const computeLed = () => {
    if (missionsLastError) return { tone: "danger", label: "LOST", color: theme.palette.error.main };
    if (!missionsLastUpdated) return { tone: "warning", label: "WAIT", color: theme.palette.warning.main };
    const age = Date.now() - missionsLastUpdated.getTime();
    if (age <= 15000) return { tone: "success", label: "LIVE", color: theme.palette.success.main };
    if (age <= 60000) return { tone: "warning", label: "SLOW", color: theme.palette.warning.main };
    return { tone: "danger", label: "LOST", color: theme.palette.error.main };
  };
  const led = computeLed();

  const handleChangeGun = (newGun) => {
    try { localStorage.setItem("guncmd_myGun", newGun); } catch { /* */ }
    setMyGunNumber(newGun);
  };

  // Send a lifecycle status update for a shot. status ∈ roger | gun_ready | shot_complete | rounds_complete
  const reportShotStatus = async (shotId, status) => {
    if (!missionId || shotId == null) return;
    const nowIso = new Date().toISOString();
    const fieldMap = {
      roger: { acknowledged: true, acknowledged_at: nowIso },
      gun_ready: { gun_ready_at: nowIso },
      shot_complete: { shot_complete_at: nowIso },
      rounds_complete: { rounds_complete_at: nowIso },
    };
    try {
      await axios.post(`${BACKEND_URL}/shot-status`, {
        mission_id: missionId,
        shot_id: shotId,
        gun_id: myGunNumber,
        status,
      });
      // Optimistic local update so the UI advances instantly.
      setShots((prev) =>
        prev.map((s) =>
          s.shot_id === shotId ? { ...s, ...(fieldMap[status] || {}) } : s,
        ),
      );
    } catch (err) {
      console.error(`Failed to report ${status}:`, err);
    }
  };

  const acknowledgeShot = async (shotId) => {
    if (!missionId || shotId == null) return;
    try {
      await axios.post(`${BACKEND_URL}/acknowledge-shot`, {
        mission_id: missionId,
        shot_id: shotId,
        gun_id: myGunNumber,
      });
      setShots((prev) =>
        prev.map((s) =>
          s.shot_id === shotId ? { ...s, acknowledged: true, acknowledged_at: new Date().toISOString() } : s,
        ),
      );
    } catch (err) {
      console.error("Failed to acknowledge shot:", err);
    }
  };

  const posMgrs = positionToMgrs(savedPosition);

  if (!myGunNumber) return null;

  const isDark = theme.palette.mode === "dark";

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 0.75, sm: 2 }, px: { xs: 1, sm: 3 } }}>
      <style>{`
        @keyframes gunPulse { 0%{transform:scale(1)} 20%{transform:scale(1.006)} 48%{transform:scale(1.012)} 100%{transform:scale(1)} }
        .pulseCard { animation: gunPulse 850ms ease-out; }
      `}</style>

      {/* ── GPS DRIFT WARNING ── */}
      {gps.needsUpdate && (
        <Alert
          severity="warning" icon={<WarningAmberRoundedIcon fontSize="small" />}
          action={<Button size="small" color="warning" onClick={() => setPosDialogOpen(true)}>Update</Button>}
          sx={{ mb: 1, py: 0, fontWeight: 600, fontSize: 12, border: "1px solid", borderColor: alpha(theme.palette.warning.main, 0.3) }}
        >
          GPS ~{gps.drift}m from saved position
        </Alert>
      )}

      {/* ── NEW ELEMENTS BANNER ── */}
      {newElementsBanner && (
        <Alert severity="warning" sx={{ mb: 1, py: 0.25, fontWeight: 700, fontSize: 13, border: "1px solid", borderColor: alpha(theme.palette.warning.main, 0.28) }}>
          NEW FIRE ELEMENTS — CHECK DIRECTION / QE
        </Alert>
      )}

      {/* ── ICON TOOLBAR ── */}
      <Paper
        variant="outlined"
        sx={{
          ...tacticalCardSx(theme),
          borderRadius: 99,
          px: 1,
          py: 0.35,
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 0.25, sm: 0.75 },
        }}
      >
        {/* Gun */}
        <Tooltip title={`Gun ${myGunNumber} — tap to change`} arrow>
          <IconButton onClick={() => setGunDialogOpen(true)} size="small" sx={iconBtnSx(theme)}>
            <Badge
              badgeContent={myGunNumber}
              color="default"
              sx={{ "& .MuiBadge-badge": { fontSize: 9, fontWeight: 700, minWidth: 14, height: 14, p: 0 } }}
            >
              <PrecisionManufacturingRoundedIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Position */}
        <Tooltip title={posMgrs ? `Position: ${posMgrs} — tap to update` : "No position — tap to set"} arrow>
          <IconButton onClick={() => setPosDialogOpen(true)} size="small" sx={iconBtnSx(theme)}>
            <GpsFixedRoundedIcon sx={{ fontSize: 20, color: savedPosition ? "success.main" : "error.main" }} />
          </IconButton>
        </Tooltip>

        {/* Mission */}
        <Tooltip title={missionId ? `Mission ${missionId} — tap to change` : "No mission — tap to select"} arrow>
          <IconButton onClick={() => setMissionDialogOpen(true)} size="small" sx={iconBtnSx(theme)}>
            <Badge
              variant={missionId ? "standard" : "dot"}
              badgeContent={missionId || undefined}
              color={missionId ? "primary" : "warning"}
              sx={{ "& .MuiBadge-badge": { fontSize: 8, fontWeight: 700, minWidth: 14, height: 14, p: "0 2px" } }}
            >
              <RadarRoundedIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Divider dot */}
        <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: alpha(theme.palette.text.primary, 0.2), mx: 0.25 }} />

        {/* Connection LED */}
        <Tooltip title={`Feed ${led.label}`} arrow>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: led.color, boxShadow: `0 0 0 3px ${alpha(led.color, 0.18)}`, flexShrink: 0 }} />
        </Tooltip>

        {/* Alerts toggle */}
        <Tooltip title={alertsEnabled ? "Alerts ON — tap to mute" : "Alerts OFF — tap to enable"} arrow>
          <IconButton
            size="small"
            onClick={() => setAlertsEnabled((v) => !v)}
            sx={{ ...iconBtnSx(theme), color: alertsEnabled ? "warning.main" : "text.disabled" }}
          >
            {alertsEnabled
              ? <NotificationsActiveRoundedIcon sx={{ fontSize: 19 }} />
              : <NotificationsOffRoundedIcon sx={{ fontSize: 19 }} />}
          </IconButton>
        </Tooltip>
      </Paper>

      {/* ── FIRE ELEMENTS (main content) ── */}
      <Paper
        variant="outlined"
        key={pulseKey}
        className={pulseKey ? "pulseCard" : ""}
        sx={{
          ...tacticalCardSx(theme),
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2.5,
          mb: 1.5,
          borderWidth: 1.5,
          borderColor: latest
            ? alpha(theme.palette.success.main, isDark ? 0.35 : 0.2)
            : inFollowMode
              ? alpha(theme.palette.info?.main || "#3b82f6", 0.4)
              : alpha(theme.palette.divider, 0.9),
          bgcolor: newElementsBanner
            ? alpha(theme.palette.warning.main, isDark ? 0.1 : 0.06)
            : undefined,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <CampaignRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Fire Elements</Typography>
            {displayShot && <StatusChip label={`Shot ${displayShot.shot_id}`} tone="success" />}
            {regulatingGunId && (
              <Chip
                size="small"
                label={iAmRegulating ? "YOU ARE REG" : `REG · GUN ${regulatingGunId}`}
                sx={{
                  fontWeight: 700,
                  fontSize: 10.5,
                  letterSpacing: 0.6,
                  height: 22,
                  px: 0.4,
                  bgcolor: alpha(
                    iAmRegulating ? theme.palette.success.main : theme.palette.warning.main,
                    isDark ? 0.22 : 0.14,
                  ),
                  color: iAmRegulating ? theme.palette.success.main : theme.palette.warning.main,
                  border: "1px solid",
                  borderColor: alpha(
                    iAmRegulating ? theme.palette.success.main : theme.palette.warning.main,
                    0.45,
                  ),
                }}
              />
            )}
            {displayShot?.phase && (
              <Chip
                size="small"
                label={displayShot.phase === "FFE" ? "FIRE FOR EFFECT" : "ADJUST FIRE"}
                sx={{
                  fontWeight: 700,
                  fontSize: 10.5,
                  letterSpacing: 0.6,
                  height: 22,
                  px: 0.4,
                  bgcolor: alpha(
                    displayShot.phase === "FFE" ? theme.palette.error.main : theme.palette.warning.main,
                    isDark ? 0.2 : 0.14,
                  ),
                  color: displayShot.phase === "FFE" ? theme.palette.error.main : theme.palette.warning.main,
                  border: "1px solid",
                  borderColor: alpha(
                    displayShot.phase === "FFE" ? theme.palette.error.main : theme.palette.warning.main,
                    0.45,
                  ),
                }}
              />
            )}
            {inFollowMode && (
              <Chip
                size="small"
                label={`FOLLOW · GUN ${followingShot.gun_id}`}
                sx={{
                  fontWeight: 700,
                  fontSize: 10.5,
                  letterSpacing: 0.6,
                  height: 22,
                  px: 0.4,
                  bgcolor: alpha(theme.palette.info?.main || "#3b82f6", isDark ? 0.2 : 0.14),
                  color: theme.palette.info?.main || "#3b82f6",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info?.main || "#3b82f6", 0.45),
                }}
              />
            )}
          </Stack>

          {!missionId ? (
            <Alert severity="info" sx={{ fontWeight: 700 }}>Select a mission to display fire elements.</Alert>
          ) : !displayShot ? (
            <Alert severity="info" sx={{ fontWeight: 700 }}>
              No fire elements yet. They will appear here automatically once the FDC starts the mission.
            </Alert>
          ) : (
            <>
              {/* Big direction / QE — the two values the gunner needs to lay the gun. */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                {[
                  { label: "DIRECTION", value: displayShot.fire_direction_deflection },
                  { label: "QUADRANT ELEVATION", value: displayShot.qe },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={(t) => ({
                      ...tacticalMetricSx(t),
                      ...(inFollowMode && { opacity: 0.85 }),
                    })}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{item.label}</Typography>
                    <Typography sx={{
                      fontSize: bigValueSize,
                      fontWeight: 700,
                      lineHeight: 1.02,
                    }}>{item.value ?? "—"}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Secondary metrics in the order requested:
                  Projectile · Fuze · Fuze Setting · Charge · Rounds. Weapon is omitted. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  { label: "PROJECTILE", value: displayShot.projectile },
                  { label: "FUZE", value: displayShot.fuze_type },
                  { label: "FUZE SETTING", value: displayShot.fuze_setting },
                  { label: "CHARGE", value: displayShot.charge },
                  { label: "ROUNDS", value: displayShot.rounds ?? 1, accent: true },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={(t) => ({
                      ...tacticalMetricSx(t),
                      flex: "1 1 110px",
                      minWidth: 100,
                      ...(inFollowMode && { opacity: 0.85 }),
                      ...(item.accent && {
                        borderColor: alpha(t.palette.warning.main, 0.45),
                        bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.12 : 0.07),
                      }),
                    })}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.label}</Typography>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: { xs: 18, sm: 22 },
                      lineHeight: 1.1,
                      mt: 0.25,
                      color: item.accent ? "warning.main" : undefined,
                    }}>{item.value ?? "—"}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Shot lifecycle progression. Each step advances the state on the backend.
                  When rounds == 1, "Rounds complete" is skipped because shot complete = done.
                  When this gun has no shot yet (AF mode, follow only), this whole
                  block is replaced with a passive informational note. */}
              {inFollowMode ? (
                <Box
                  sx={{
                    px: 1.5, py: 1.25,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info?.main || "#3b82f6", isDark ? 0.1 : 0.06),
                    border: "1px dashed",
                    borderColor: alpha(theme.palette.info?.main || "#3b82f6", 0.45),
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.4, color: theme.palette.info?.main || "#3b82f6" }}>
                    👀 Follow only — Gun {followingShot.gun_id} is firing
                    {regulatingGunId && String(regulatingGunId) === String(followingShot.gun_id) ? " (regulating)" : ""}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                    Stand by until the FDC switches the mission phase or assigns this gun to fire.
                  </Typography>
                </Box>
              ) : (() => {
                const totalRounds = Number(latest.rounds) || 1;
                const steps = [
                  { key: "roger", label: "ROGER", at: latest.acknowledged_at, hint: "Receipt acknowledged" },
                  { key: "gun_ready", label: "GUN READY", at: latest.gun_ready_at, hint: "Gun is laid and loaded" },
                  { key: "shot_complete", label: totalRounds > 1 ? "SHOT" : "SHOT COMPLETE", at: latest.shot_complete_at, hint: totalRounds > 1 ? "First round fired" : "Round fired" },
                ];
                if (totalRounds > 1) {
                  steps.push({ key: "rounds_complete", label: "ROUNDS COMPLETE", at: latest.rounds_complete_at, hint: `All ${totalRounds} rounds fired` });
                }

                // Find the index of the next step that hasn't been reported yet.
                const nextIdx = steps.findIndex((s) => !s.at);

                return (
                  <Stack spacing={0.75} sx={{ pt: 0.25 }}>
                    {steps.map((step, idx) => {
                      const completed = !!step.at;
                      const isNext = idx === nextIdx;
                      const isFuture = !completed && !isNext;

                      if (completed) {
                        return (
                          <Stack
                            key={step.key}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              px: 1.5,
                              py: 0.7,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.success.main, isDark ? 0.16 : 0.1),
                              border: "1px solid",
                              borderColor: alpha(theme.palette.success.main, 0.4),
                            }}
                          >
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: theme.palette.success.main, flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.4, color: theme.palette.success.main, flex: 1 }}>
                              ✓ {step.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: theme.palette.success.main }}>
                              {(() => {
                                const d = new Date(step.at);
                                const hh = String(d.getHours()).padStart(2, "0");
                                const mm = String(d.getMinutes()).padStart(2, "0");
                                const ss = String(d.getSeconds()).padStart(2, "0");
                                return `${hh}:${mm}:${ss}`;
                              })()}
                            </Typography>
                          </Stack>
                        );
                      }

                      if (isNext) {
                        return (
                          <Button
                            key={step.key}
                            variant="contained"
                            size="large"
                            onClick={() => reportShotStatus(latest.shot_id, step.key)}
                            sx={{
                              fontWeight: 700,
                              fontSize: 17,
                              py: 1.4,
                              borderRadius: 1.75,
                              letterSpacing: 0.6,
                              bgcolor: theme.palette.warning.main,
                              color: theme.palette.warning.contrastText,
                              "&:hover": { bgcolor: theme.palette.warning.dark },
                              boxShadow: `0 4px 14px ${alpha(theme.palette.warning.main, 0.45)}`,
                            }}
                          >
                            ▶ {step.label}
                          </Button>
                        );
                      }

                      // Future step: muted preview
                      return (
                        <Stack
                          key={step.key}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            px: 1.5,
                            py: 0.7,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.025),
                            border: "1px dashed",
                            borderColor: alpha(theme.palette.divider, 0.95),
                            opacity: 0.7,
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: alpha(theme.palette.text.disabled, 0.5), flexShrink: 0 }} />
                          <Typography sx={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.4, color: "text.secondary", flex: 1 }}>
                            {step.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 600 }}>
                            {step.hint}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                );
              })()}
            </>
          )}
        </Stack>
      </Paper>

      {/* ── HISTORY (compact table) ── */}
      <Paper
        variant="outlined"
        sx={{ ...tacticalCardSx(theme), p: 0, borderRadius: 2.5, overflow: "hidden" }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1 }}>
          <HistoryRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>History</Typography>
          {myShots.length > 0 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {myShots.length} element{myShots.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Stack>

        {!missionId ? (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 1.5, pb: 1.5 }}>
            Select a mission to see history.
          </Typography>
        ) : !myShots.length ? (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 1.5, pb: 1.5 }}>
            No history yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table size="small" sx={{ "& td, & th": { borderBottom: 0 }, minWidth: 640 }}>
            <TableHead>
              <TableRow>
                {[
                  { label: "Shot", align: "left" },
                  { label: "Status", align: "left" },
                  { label: "Dir", align: "right" },
                  { label: "QE", align: "right" },
                  { label: "Proj", align: "left" },
                  { label: "Fuze", align: "left" },
                  { label: "Setting", align: "right" },
                  { label: "Charge", align: "left" },
                  { label: "Rds", align: "right" },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    align={col.align}
                    sx={{
                      py: 0.7, px: 1,
                      fontWeight: 600, fontSize: 10.5, letterSpacing: 0.7,
                      textTransform: "uppercase",
                      color: alpha(theme.palette.text.primary, 0.7),
                      bgcolor: alpha(theme.palette.text.primary, isDark ? 0.05 : 0.025),
                      borderBottom: "1px solid",
                      borderBottomColor: alpha(theme.palette.divider, 0.9),
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // History lists ONLY this gun's shots; the mission-wide list stays
                // in `shots` for the follow-mode fire-elements card above.
                // Compute per-gun ordinal: oldest shot is #1.
                const ascending = myShots.slice().sort((a, b) => (a.shot_id ?? 0) - (b.shot_id ?? 0));
                const ordinal = new Map(ascending.map((s, i) => [s.shot_id, i + 1]));
                return myShots.slice(0, 20).map((s) => {
                  const cellSx = {
                    py: 0.85, px: 1,
                    fontSize: 13,
                    fontVariantNumeric: "tabular-nums",
                    borderBottom: "1px solid",
                    borderBottomColor: alpha(theme.palette.divider, 0.6),
                    whiteSpace: "nowrap",
                  };
                  return (
                  <TableRow key={`${s.mission_id}-${s.shot_id}-${s.gun_id}`}>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>#{ordinal.get(s.shot_id) ?? s.shot_id}</TableCell>
                    <TableCell
                      sx={{ ...cellSx, color: "text.secondary", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTimelineShotId((prev) => (prev === s.shot_id ? null : s.shot_id));
                      }}
                    >
                      {(() => {
                        const latest = latestStageOf(s);
                        if (!latest) return formatTimeOnly(s.created_at);
                        const totalRounds = Number(s.rounds) || 1;
                        const lines = SHOT_TIMELINE_STAGES
                          .filter((st) => !(st.key === "rounds_complete_at" && totalRounds <= 1))
                          .map((st) => ({
                            label: st.label,
                            time: s[st.key] ? formatTimeOnly(s[st.key]) : "—",
                            reached: !!s[st.key],
                          }));
                        const isOpen = openTimelineShotId === s.shot_id;
                        const stageColor =
                          latest.key === "rounds_complete_at" || latest.key === "shot_complete_at"
                            ? theme.palette.success.main
                            : "text.secondary";
                        return (
                          <Tooltip
                            arrow
                            placement="bottom-end"
                            open={isOpen}
                            onClose={() => setOpenTimelineShotId((prev) => (prev === s.shot_id ? null : prev))}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            slotProps={{
                              popper: {
                                modifiers: [
                                  { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                                  { name: "flip", options: { fallbackPlacements: ["top-end", "bottom-start", "top-start"] } },
                                ],
                              },
                            }}
                            title={
                              <Box sx={{ minWidth: 180, py: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.5, color: "rgba(255,255,255,0.85)" }}>
                                  Shot Timeline · #{ordinal.get(s.shot_id) ?? s.shot_id}
                                </Typography>
                                {lines.map((l) => (
                                  <Stack
                                    key={l.label}
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={2}
                                    sx={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      fontVariantNumeric: "tabular-nums",
                                      opacity: l.reached ? 1 : 0.45,
                                      py: 0.15,
                                    }}
                                  >
                                    <span>{l.reached ? "✓" : "·"} {l.label}</span>
                                    <span>{l.time}</span>
                                  </Stack>
                                ))}
                                <Typography
                                  sx={{
                                    mt: 0.5, pt: 0.5,
                                    fontSize: 10,
                                    fontStyle: "italic",
                                    opacity: 0.7,
                                    borderTop: "1px solid rgba(255,255,255,0.15)",
                                  }}
                                >
                                  Tap again to close
                                </Typography>
                              </Box>
                            }
                          >
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 0.7, py: 0.2,
                                borderRadius: 0.75,
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                textTransform: "uppercase",
                                bgcolor: alpha(
                                  latest.key === "rounds_complete_at" || latest.key === "shot_complete_at"
                                    ? theme.palette.success.main
                                    : theme.palette.text.primary,
                                  isDark ? 0.18 : 0.1,
                                ),
                                color: latest.key === "rounds_complete_at" || latest.key === "shot_complete_at"
                                  ? theme.palette.success.main
                                  : theme.palette.text.primary,
                                border: "1px solid",
                                borderColor: alpha(
                                  latest.key === "rounds_complete_at" || latest.key === "shot_complete_at"
                                    ? theme.palette.success.main
                                    : theme.palette.divider,
                                  0.4,
                                ),
                              }}
                            >
                              {latest.label}
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">{s.fire_direction_deflection ?? "—"}</TableCell>
                    <TableCell sx={cellSx} align="right">{s.qe ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>{s.projectile ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>{s.fuze_type ?? "—"}</TableCell>
                    <TableCell sx={cellSx} align="right">{s.fuze_setting ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>{s.charge ?? "—"}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: theme.palette.warning.main }} align="right">{s.rounds ?? 1}</TableCell>
                  </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <GunDialog
        open={gunDialogOpen}
        onClose={() => setGunDialogOpen(false)}
        current={myGunNumber}
        onChange={handleChangeGun}
      />
      <PositionDialog
        open={posDialogOpen}
        onClose={() => setPosDialogOpen(false)}
        gunNumber={myGunNumber}
        onSaved={() => { setPositionVersion((v) => v + 1); gps.recheck(); }}
      />
      <MissionDialog
        open={missionDialogOpen}
        onClose={() => setMissionDialogOpen(false)}
        current={missionId}
        options={missionOptions}
        onChange={(v) => { setMissionId(v); setShots([]); setStatusMsg(""); }}
      />
    </Container>
  );
}
